/**
 * @fileoverview Public layout route that provides the structure for public pages
 * Includes header, main content area, and footer components
 */

// Library imports
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

// Shared components
import Footer from "@/components/shared/footer";
import Header from "@/components/shared/header";
import { PublicAppearanceProvider } from "@/providers/public-appearance.provider";

/**
 * Public layout component that provides the structure for public routes
 */
function PublicLayout() {
  return (
    <PublicAppearanceProvider
      className="flex min-h-screen w-full flex-col"
      role="region"
      aria-label="Public content layout"
    >
      <Header />

      <main className="mx-auto w-full max-w-7xl flex-1" role="main" aria-label="Main content area">
        <Outlet />
      </main>

      <Footer />
    </PublicAppearanceProvider>
  );
}

// Route configuration using TanStack Router
export const Route = createFileRoute("/(public)/_public")({
  // Marketing, free tools and public pages now live in the dedicated SEO site (frontend/apps/web).
  // Control-plane is the authenticated product app, so every legacy public/marketing/tools URL is
  // sent into the app; the dashboard route guard bounces unauthenticated users to /login.
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
  component: PublicLayout,
  // Add error boundary handling for public routes
  errorComponent: ({ error }: { error: Error }) => (
    <div role="alert" className="p-4">
      <h1>Error</h1>
      <pre>{error.message}</pre>
    </div>
  ),
});
